/**
 * Credit Cost Configuration
 *
 * Centralized configuration for credit costs across all features.
 * Values are read from environment variables with sensible defaults.
 *
 * All variables are prefixed with NEXT_PUBLIC_ to make them available
 * in the frontend for UI displays and marketing copy.
 */

import { type CitationPlanBucket, getEntitlements } from "./plan-entitlements";

/**
 * Per-plan allowance constants live in `plan-entitlements.ts` (so that module
 * has no imports and this one can wrap it without a cycle). Re-exported here
 * under the same names so every existing import keeps working.
 */
export {
	AGENCY_CORE_CITATION_REWRITES_PER_MONTH,
	AGENCY_CORE_CITATION_SCORING_PAGES_PER_MONTH,
	AGENCY_CORE_INCLUDED_PROMPTS,
	AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH,
	AGENCY_CORE_V2_CITATION_REWRITES_PER_MONTH,
	AGENCY_CORE_V2_CITATION_SCORING_PAGES_PER_MONTH,
	AGENCY_CORE_V2_INCLUDED_PROMPTS,
	AGENCY_CORE_V2_SITE_AUDIT_PAGES_PER_MONTH,
	AGENCY_PRO_CITATION_REWRITES_PER_MONTH,
	AGENCY_PRO_CITATION_SCORING_PAGES_PER_MONTH,
	AGENCY_PRO_INCLUDED_PROMPTS,
	AGENCY_PRO_SITE_AUDIT_PAGES_PER_MONTH,
	BRAND_CITATION_REWRITES_PER_MONTH,
	BRAND_CITATION_SCORING_PAGES_PER_MONTH,
	BRAND_INCLUDED_PROMPTS,
	BRAND_SITE_AUDIT_PAGES_PER_MONTH,
	BRAND_V2_CITATION_REWRITES_PER_MONTH,
	BRAND_V2_CITATION_SCORING_PAGES_PER_MONTH,
	BRAND_V2_INCLUDED_PROMPTS,
	BRAND_V2_SITE_AUDIT_PAGES_PER_MONTH,
	FREE_CITATION_SCORING_PAGES_PER_MONTH,
} from "./plan-entitlements";

/**
 * AI Visibility Report generation cost
 * Default: 15 credits per report
 */
export const AI_REPORT_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_AI_REPORT_UNLOCK_CREDITS || "15",
	10,
);

/**
 * Prompt-run billing: number of nightly dispatches of one tracked prompt that
 * one credit buys, billed in advance. Applies to prompts NOT covered by a
 * plan's bundled allocation (PAYG prompts and subscription overage prompts).
 * A "run" is one nightly dispatch of a query regardless of platform count or
 * location fan-out. Default: 30 runs per credit (~1 credit/month per prompt).
 */
export const PROMPT_RUNS_PER_CREDIT = Number.parseInt(
	process.env.NEXT_PUBLIC_PROMPT_RUNS_PER_CREDIT || "30",
	10,
);

/**
 * Competitor brand consistency check cost
 * When running a brand consistency check, debit 1 credit per competitor tracked
 * Default: 1 credit per competitor tracked
 */
export const COMPETITOR_BRAND_CONSISTENCY_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_COMPETITOR_BRAND_CONSISTENCY_CREDITS || "1",
	10,
);

/**
 * FAQ generation cost — flat rate per run (regardless of how many FAQs are generated).
 * Refunded if the run errors or produces 0 FAQs.
 * Default: 1 credit per run
 */
export const FAQ_RUN_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_FAQ_GENERATION_CREDITS || "1",
	10,
);

/** @deprecated Use FAQ_RUN_CREDITS instead — kept for backward compatibility */
export const FAQ_ITEM_CREDITS = FAQ_RUN_CREDITS;

/**
 * AI visit tracking ratio
 * Default: 500 visits = 1 credit (1 credit per 500 AI visits)
 */
export const AI_VISITS_PER_CREDIT = Number.parseInt(
	process.env.NEXT_PUBLIC_AI_VISITS_PER_CREDIT || "500",
	10,
);

/**
 * Free AI visit allowance per property per month.
 * Visits up to this number are tracked at no cost, regardless of plan or
 * credit balance. Visits above this threshold are billed at the standard
 * AI_VISITS_PER_CREDIT ratio (1 credit per 500 visits by default).
 *
 * Applies to every property — PAYG, trialing, and subscription alike.
 */
export const FREE_AI_VISITS_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_FREE_MONTHLY_AI_VISITS || "25000",
	10,
);

/**
 * Default monthly max credits for AI visit tracking per property
 * This prevents unexpectedly large bills if a site gets heavy AI traffic
 * Default: 30 credits per month (= 15,000 AI visits at 500 visits/credit)
 */
export const DEFAULT_AI_VISITS_MONTHLY_MAX_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_DEFAULT_ANALYTICS_MONTHLY_MAX_CREDITS || "30",
	10,
);

/**
 * Report unlock cost
 * Default: 15 credits to unlock an AI Visibility Report
 * Note: This is the same as AI_REPORT_CREDITS but exported separately for clarity
 */
export const REPORT_UNLOCK_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_AI_REPORT_UNLOCK_CREDITS || "15",
	10,
);

/**
 * Credit grant after paid report signup
 * Default: 29 credits granted when guest who purchased a report signs up
 * This covers the cost of the report (15) plus extra credits (14)
 */
export const CREDIT_GRANT_AFTER_PAID_REPORT_SIGNUP = Number.parseInt(
	process.env.NEXT_PUBLIC_CREDIT_GRANT_AFTER_PAID_REPORT_SIGNUP || "29",
	10,
);

// ============================================================================
// PROMPT COSTS
// ============================================================================

// ============================================================================
// TRACKED PROMPT OVERAGE COSTS
// ============================================================================

/**
 * Overage cost per tracked prompt for subscription plans (Brand & Agency).
 * Subscribers get their bundled allocation free; each additional tracked prompt
 * beyond the allocation costs this many credits (billed as overage).
 * Default: 1 credit per overage prompt ($1)
 */
export const TRACKED_PROMPT_OVERAGE_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_TRACKED_PROMPT_OVERAGE_CREDITS || "1",
	10,
);

// ============================================================================
// SITE AUDIT COSTS
// ============================================================================

/**
 * Site audit pages per credit (PAYG billing)
 * Default: 50 pages per credit, minimum 1 credit per audit
 */
export const SITE_AUDIT_PAGES_PER_CREDIT = Number.parseInt(
	process.env.NEXT_PUBLIC_SITE_AUDIT_PAGES_PER_CREDIT || "50",
	10,
);

// ============================================================================
// CITATION OPTIMIZER COSTS
// ============================================================================

/**
 * AI Search Citation Optimizer.
 *
 * Scoring a page is FREE for everyone (it's the acquisition/demo surface and
 * costs almost nothing to deliver). The only billable action is a REWRITE,
 * which includes unlimited re-scoring of that page.
 *
 * Billing mirrors site audits: subscription plans (Brand/Agency) get a bundled
 * monthly rewrite allowance; overage (and PAYG) is charged per rewrite.
 */

/**
 * Credits per page rewrite (PAYG, and per-rewrite overage for subscribers).
 * Default: 10 credits ($10). Anchored well below the closest competitor's
 * per-draft cost; one rewrite includes unlimited re-scoring of that page.
 */
export const CITATION_REWRITE_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_CITATION_REWRITE_CREDITS || "10",
	10,
);

/**
 * Calculate credits needed for `count` page rewrites (PAYG / overage).
 * Flat per-rewrite rate. Returns 0 for non-positive counts.
 */
export function calculateCitationRewriteCredits(count: number): number {
	if (count <= 0) return 0;
	return count * CITATION_REWRITE_CREDITS;
}

/**
 * Credits per keyword brief (the outline a writer works from).
 *
 * Default: 5 credits, half a rewrite. A brief costs one large model call
 * against a handful of searches, where a rewrite costs a call over a whole
 * page plus the re-scoring that comes with it, so it is priced below one.
 *
 * A starting value, not a measured one (decision R4): it is expected to be
 * re-set once the cost ledger has real numbers, which is why it is env-tunable.
 */
export const CITATION_OUTLINE_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_CITATION_OUTLINE_CREDITS || "5",
	10,
);

/**
 * Calculate credits needed for `count` briefs (PAYG / overage).
 * Flat per-brief rate. Returns 0 for non-positive counts.
 */
export function calculateCitationOutlineCredits(count: number): number {
	if (count <= 0) return 0;
	return count * CITATION_OUTLINE_CREDITS;
}

// ============================================================================
// PLACEMENT SCORING (AIPQS)
// ============================================================================

/**
 * Credits per placement scored.
 *
 * **Currently free by product decision** (default 0), and the debit path
 * no-ops at zero. Placement scoring is how AIPQS becomes the standard metric
 * in PR, so the metric is given away; the PR-firm engagements that use it in
 * volume are billed by contract (an attribution clause), not by this constant.
 *
 * The plumbing exists so that pricing it later is a config change rather than
 * a code change: set `NEXT_PUBLIC_PLACEMENT_SCORE_CREDITS` and every scored
 * placement starts debiting. Flipping it also obliges a copy pass — several
 * surfaces currently say scoring is free.
 */
export const PLACEMENT_SCORE_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_PLACEMENT_SCORE_CREDITS || "0",
	10,
);

/**
 * Optional ceiling on placements one organization may score per calendar month.
 *
 * `null` when unset, empty or non-numeric — which is the default, so **cap
 * enforcement is inert**: `checkPlacementScoringCap` allows everything and the
 * usage endpoint reports "no cap". It exists so an abusive volume can be
 * stopped with an env var instead of a deploy.
 */
export const PLACEMENT_SCORING_MONTHLY_CAP: number | null = (() => {
	const raw = process.env.NEXT_PUBLIC_PLACEMENT_SCORING_MONTHLY_CAP?.trim();
	if (!raw) return null;
	const parsed = Number.parseInt(raw, 10);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
})();

/**
 * Credits for `count` scored placements. Zero while scoring is free, so
 * callers can compute unconditionally and let the debit no-op.
 */
export function calculatePlacementScoreCredits(count: number): number {
	if (count <= 0) return 0;
	return count * PLACEMENT_SCORE_CREDITS;
}

// ============================================================================
// TRIAL CONSTANTS
// ============================================================================

/**
 * Promotional credit grant for new users (free trial credits).
 * Default: 30 credits
 */
export const TRIAL_CREDITS = Number.parseInt(
	process.env.NEXT_PUBLIC_PROMOTIONAL_CREDIT_GRANT || "30",
	10,
);

/**
 * Promotional credit expiration in days.
 * Default: 14 days
 */
export const TRIAL_DAYS = Number.parseInt(
	process.env.NEXT_PUBLIC_PROMOTIONAL_CREDIT_EXPIRATION_DAYS || "14",
	10,
);

/**
 * Grace days added to a subscription credit bundle's one-month life.
 *
 * A bundle is use-or-lose within the billing month, but its replacement can
 * only be granted once the renewal invoice is PAID — and that always lands
 * after the old bundle's one-month mark: Stripe finalizes a renewal invoice up
 * to ~an hour after the period ends, then charges, then delivers the webhook;
 * quarterly/annual bundles are dripped by an hourly cron and so are late by
 * construction; a dunning retry on a soft decline pushes it out by days.
 *
 * Without grace the balance is genuinely 0 across that seam, which fires
 * "you're out of credits" alarms at exactly the moment a customer renewed.
 * The overlap costs little: FIFO consumes soonest-expiring first, so the stale
 * bundle still drains ahead of the fresh one, and the only credits that
 * survive the seam are ones the customer already paid for and hadn't spent.
 *
 * Default: 3 days
 */
export const SUBSCRIPTION_GRANT_GRACE_DAYS = Number.parseInt(
	process.env.NEXT_PUBLIC_SUBSCRIPTION_GRANT_GRACE_DAYS || "3",
	10,
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Helper function to calculate credits needed for AI visits.
 *
 * Applies the FREE_AI_VISITS_PER_MONTH free tier: the first N visits in a
 * month are free, only visits beyond that consume credits. The caller is
 * responsible for passing the property's running monthly total — passing
 * raw batch sizes will double-count the free allowance.
 *
 * @param visitCount Total AI visits this month (running total, not batch size)
 * @returns Number of credits needed (rounded up). 0 if under free tier.
 */
export function calculateAiVisitCredits(visitCount: number): number {
	if (visitCount <= 0) return 0;
	const billable = visitCount - FREE_AI_VISITS_PER_MONTH;
	if (billable <= 0) return 0;
	return Math.ceil(billable / AI_VISITS_PER_CREDIT);
}

/**
 * Credits needed for one FAQ generation run.
 * Flat rate regardless of FAQ count — refunded on error or 0 results.
 */
export function calculateFaqCredits(_faqCount?: number): number {
	return FAQ_RUN_CREDITS;
}

/**
 * Calculate credits needed for a site audit (PAYG model).
 * Always at least 1 credit, rounded up to the next whole credit.
 * Unused pages within a credit block are "wasted".
 *
 * @param pageCount Number of pages to audit
 * @returns Number of credits needed (minimum 1)
 */
export function calculateSiteAuditCredits(pageCount: number): number {
	if (pageCount <= 0) return 0;
	return Math.max(1, Math.ceil(pageCount / SITE_AUDIT_PAGES_PER_CREDIT));
}

// ============================================================================
// PROMPT ALLOCATION
// ============================================================================

/**
 * Get the included prompts for a given plan.
 * Returns 0 for plans without a prompt allocation (e.g., PAYG).
 *
 * @param planType - The organization's subscriptionPlanType
 * @returns Number of included prompts, or 0 if plan doesn't include one
 */
export function getIncludedPrompts(
	planType: string | null | undefined,
): number {
	return getEntitlements(planType).trackedPrompts;
}

/**
 * Calculate overage credits required to add tracked prompts.
 * Only applies to subscription plans (Brand/Agency). PAYG users cannot
 * use tracked prompts — they must subscribe.
 *
 * @param newPromptCount - Number of new prompts being added
 * @param currentTrackedCount - Prompts already tracked org-wide (across all active projects)
 * @param planType - The organization's subscriptionPlanType
 * @returns Credits required for overage prompts (0 if within allocation)
 */
export function calculateTrackedPromptCredits(
	newPromptCount: number,
	currentTrackedCount: number,
	planType: string | null | undefined,
): number {
	if (newPromptCount <= 0) return 0;

	const allocation = getIncludedPrompts(planType);

	if (allocation === Number.POSITIVE_INFINITY) return 0;
	if (allocation === 0) return 0;

	const freeRemaining = Math.max(0, allocation - currentTrackedCount);
	const overagePrompts = Math.max(0, newPromptCount - freeRemaining);
	return overagePrompts * TRACKED_PROMPT_OVERAGE_CREDITS;
}

// ============================================================================
// SITE AUDIT PAGE ALLOCATION
// ============================================================================
/**
 * Get the monthly site audit page allocation for a given plan.
 * Returns 0 for plans without a page allocation (e.g., PAYG).
 *
 * @param planType - The organization's subscriptionPlanType
 * @returns Monthly page allocation, or 0 if plan doesn't include one
 */
export function getSiteAuditPageAllocation(
	planType: string | null | undefined,
): number {
	return getEntitlements(planType).siteAuditPages;
}

// ============================================================================
// CITATION REWRITE ALLOCATION
// ============================================================================
/**
 * Get the monthly citation-rewrite allocation for a given plan.
 * Returns 0 for plans without an allocation (e.g. PAYG) — they pay per rewrite.
 *
 * @param planType - The organization's subscriptionPlanType
 * @returns Monthly rewrite allocation, or 0 if the plan doesn't include one
 */
export function getCitationRewriteAllocation(
	planType: string | null | undefined,
): number {
	return getEntitlements(planType).citationRewrites;
}

// ============================================================================
// CITATION SCORING (free, metered)
// ============================================================================
/**
 * Scoring a page stays FREE of credit charges (decision D9) — it is the
 * acquisition surface. What it gets instead is a **monthly page meter** per
 * plan: breadth is gated, access is not. Over the cap the answer is an upgrade
 * prompt or a wait, never a charge.
 *
 * The unit is a PAGE, not a run (decision D5): the same page re-run against a
 * different search set still counts once in a calendar month, and re-scoring a
 * rewrite never counts at all. That keeps the natural score → revise → re-score
 * loop from eating somebody's allowance.
 *
 * Starting values are literature-informed guesses against estimated unit cost
 * (roughly 1–3¢ for a ChatGPT run, 8–15¢ for a cold four-platform page), so
 * every one of them is env-tunable and is expected to be re-set once the cost
 * ledger has 30 days of real numbers (decision R3).
 */

/**
 * Get the monthly citation-scoring PAGE allocation for a plan.
 *
 * Unlike the rewrite allocation, there is no "0" case: everyone can score,
 * including free/trial/PAYG — they just get the smallest meter. Enterprise is
 * unlimited (fair use), reported as Infinity exactly like the other
 * allocations, so callers must cap it before putting it in a JSON response.
 *
 * @param planType - The organization's subscriptionPlanType
 * @returns Monthly page allocation (Infinity for enterprise)
 */
export function getCitationScoringPageAllocation(
	planType: string | null | undefined,
): number {
	return getEntitlements(planType).citationScoringPages;
}

/**
 * Every assistant pipeline the Citation Optimizer can score.
 *
 * Deliberately PLAIN STRINGS, duplicated from `CitationPlatform` in
 * `@repo/core` rather than imported: `@repo/payments` must never import
 * `@repo/core` (that edge is the circular dependency this repo forbids), and
 * this module is also part of the browser-safe `@repo/payments/client` entry.
 * Core owns the authoritative union; a mismatch shows up as a rejected platform
 * at the run-creation boundary, not as silent drift.
 */
export const CITATION_PLATFORMS = [
	"chatgpt",
	"google_aio",
	"google_aim",
	"claude",
] as const;

/**
 * Which pipelines a plan may score.
 *
 * The free tier gets ChatGPT only (decision R7): one pipeline, one search, so
 * the taste of the product costs cents. Everyone paying gets all four.
 */
export const CITATION_ALLOWED_PLATFORMS_BY_PLAN: Record<
	"free" | "brand" | "agency" | "enterprise",
	readonly string[]
> = {
	free: ["chatgpt"],
	brand: CITATION_PLATFORMS,
	agency: CITATION_PLATFORMS,
	enterprise: CITATION_PLATFORMS,
};

/** The plan bucket a subscriptionPlanType falls into for citation gating. */
export function getCitationPlanBucket(
	planType: string | null | undefined,
): CitationPlanBucket {
	return getEntitlements(planType).citationBucket;
}

/** The pipelines this plan may score, as plain platform strings. */
export function getCitationAllowedPlatforms(
	planType: string | null | undefined,
): readonly string[] {
	return CITATION_ALLOWED_PLATFORMS_BY_PLAN[getCitationPlanBucket(planType)];
}
